// services/userManagementService.ts - COMPLETE FIXED VERSION
import { api } from '@edemics/lib/api';

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  status: string;
  userType: string;
  isEnabled: boolean;
  role: string;
  tenantId: string;
  branchId?: string;
  branch?: {
    id: string;
    name: string;
  };
  tenant?: {
    id: string;
    name: string;
    subdomain: string;
  };
  metadata?: any;
}

export interface NonUserItem {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  admissionNumber?: string;
  employeeId?: string;
  className?: string;
  sectionName?: string;
  type?: string;
  department?: {
    id: string;
    name: string;
  };
  branch?: {
    id: string;
    name: string;
  };
  children?: Array<{
    name: string;
    class: string;
  }>;
  isTeacher?: boolean;
  suggestedEmail?: string;
  hasEmail?: boolean;
  canConvert?: boolean;
}

export interface StaffPickerItem {
  id: string;
  source: 'user' | 'employee';
  name: string;
  email: string;
  role: string;
  isTeacher: boolean;
  employeeId?: string;
  position?: string;
  department?: { id: string; name: string } | null;
  branch?: { id: string; name: string } | null;
  subtitle: string;
}

export interface UserDetails extends User {
  firstName: string;
  middleName: string;
  lastName: string;
  username: string;
  language: string;
  timeZone: string;
  enabled: boolean;
  roleProfile: string;
  roles: { value: string; label: string; checked: boolean }[];
  allRoles: string[];
  connections: {
    profile: { contact: number; blogger: number };
    logs: { accessLog: number; activityLog: number; energyPointLog: number; routeHistory: number };
    settings: { userPermission: number; documentFollow: number };
    activity: { communication: number; toDo: number };
    integrations: { tokenCache: number };
  };
  assignedTo: string;
  attachments: any[];
  sharedWith: any[];
  tags: any[];
  studentData?: any;
  parentData?: any;
  employeeData?: any;
  parentId?: string;
  childrenCount?: number;
  employeeId?: string;
}

export interface BulkConvertResult {
  success: number;
  failed: number;
  results: Array<{
    id: string;
    name: string;
    email: string;
    userType: string;
    temporaryPassword: string;
    mandatePasswordChange: boolean;
  }>;
  errors: Array<{
    id: string;
    error: string;
  }>;
}

export interface TimelineItem {
  id: string;
  userId: string;
  createdBy: string;
  actionType: string;
  actionDetails: any;
  metadata?: any;
  timestamp: string;
  createdAt: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface CommentData {
  content: string;
  attachments?: Array<{ type: 'image' | 'file'; data: string | File; name?: string }>;
  mentionedUsers?: string[];
  isPrivate?: boolean;
}

export interface AttachmentData {
  id: string;
  userId: string;
  fileName: string;
  fileType: string;
  fileSize: string;
  fileUrl: string;
  description?: string;
  uploadedBy: string;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
}
export interface SwitcherOption {
  value: string;
  label: string;
  subtitle: string;
}
class UserManagementService {
  private tenantId: string | null = null;
  private userId: string | null = null;

  constructor() {
    this.initializeUserData();
  }

  private initializeUserData() {
    if (typeof window !== 'undefined') {
      const userData = sessionStorage.getItem('user');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          this.tenantId = user.tenantId || user.id || 'default-tenant';
          this.userId = user.id;
        } catch (error) {
          console.error('Failed to parse user data:', error);
          this.tenantId = 'default-tenant';
        }
      }
    }
  }

  private getHeaders() {
    return {
      'Content-Type': 'application/json',
      'X-Tenant-ID': this.tenantId || 'default-tenant',
      'Authorization': `Bearer ${sessionStorage.getItem('token') || ''}`,
    };
  }

  private getFormDataHeaders() {
    return {
      'X-Tenant-ID': this.tenantId || 'default-tenant',
      'Authorization': `Bearer ${sessionStorage.getItem('token') || ''}`,
    };
  }

  // Get all users (existing users only)
  async getAllUsers(): Promise<User[]> {
    try {
      const response = await api.get('/user-management/users', {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch users');
    }
  }

  // V5 §1.7 — Comms staff picker: User ∪ Employee deduped by email.
  // Powers Meeting Mgt Presenter/User fields and Chat "Other Users"
  // so the uploaded staff (Employee rows without login) are selectable.
  async getStaffPicker(
    opts: { search?: string; teachersOnly?: boolean } = {},
  ): Promise<StaffPickerItem[]> {
    try {
      const params: Record<string, string> = {};
      if (opts.search) params.search = opts.search;
      if (opts.teachersOnly) params.teachersOnly = 'true';
      const response = await api.get('/user-management/staff-picker', {
        headers: this.getHeaders(),
        params,
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch staff picker:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch staff picker');
    }
  }

  // Get students without user accounts
  async getNonUserStudents(searchTerm?: string): Promise<NonUserItem[]> {
    try {
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await api.get('/user-management/non-user-students', {
        headers: this.getHeaders(),
        params,
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch non-user students:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch non-user students');
    }
  }

  // Get parents without user accounts
  async getNonUserParents(searchTerm?: string): Promise<NonUserItem[]> {
    try {
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await api.get('/user-management/non-user-parents', {
        headers: this.getHeaders(),
        params,
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch non-user parents:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch non-user parents');
    }
  }

  // Get employees without user accounts
  async getNonUserEmployees(searchTerm?: string): Promise<NonUserItem[]> {
    try {
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await api.get('/user-management/non-user-employees', {
        headers: this.getHeaders(),
        params,
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch non-user employees:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch non-user employees');
    }
  }

  // Convert student to user
  async convertStudentToUser(
    studentId: string,
    password: string,
    email?: string
  ): Promise<User> {
    try {
      const response = await api.post(
        `/user-management/convert/student/${studentId}`,
        { password, email },
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to convert student to user:', error);
      throw new Error(error.response?.data?.message || 'Failed to convert student to user');
    }
  }

  // Convert parent to user
  async convertParentToUser(
    parentId: string,
    password: string,
    email?: string
  ): Promise<User> {
    try {
      const response = await api.post(
        `/user-management/convert/parent/${parentId}`,
        { password, email },
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to convert parent to user:', error);
      throw new Error(error.response?.data?.message || 'Failed to convert parent to user');
    }
  }

  // Convert employee to user
  async convertEmployeeToUser(
    employeeId: string,
    password: string,
    role?: string
  ): Promise<User> {
    try {
      const response = await api.post(
        `/user-management/convert/employee/${employeeId}`,
        { password, role },
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to convert employee to user:', error);
      throw new Error(error.response?.data?.message || 'Failed to convert employee to user');
    }
  }

  // Bulk convert to users
  async bulkConvertToUsers(data: {
    type: 'student' | 'parent' | 'employee';
    ids: string[];
    passwordOption: 'same' | 'unique';
    defaultPassword?: string;
    mandatePasswordChange: boolean;
  }): Promise<BulkConvertResult> {
    try {
      const response = await api.post(
        '/user-management/bulk-convert',
        data,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to bulk convert users:', error);
      throw new Error(error.response?.data?.message || 'Failed to bulk convert users');
    }
  }

  // Get user by ID with full details
  async getUserById(userId: string): Promise<UserDetails> {
    try {
      const response = await api.get(`/user-management/users/${userId}`, {
        headers: this.getHeaders(),
      });
      return this.transformUserData(response.data);
    } catch (error: any) {
      console.error('Failed to fetch user:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch user');
    }
  }

  // Update user status (enable/disable)
  async updateUserStatus(userId: string, enabled: boolean): Promise<any> {
    try {
      const response = await api.put(
        `/user-management/users/${userId}/status`,
        { enabled },
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to update user status:', error);
      throw new Error(error.response?.data?.message || 'Failed to update user status');
    }
  }

  // Update user password
  async updateUserPassword(userId: string, passwordData: {
    newPassword: string;
    logoutAllDevices?: boolean;
    requirePasswordChange?: boolean;
  }): Promise<any> {
    try {
      const response = await api.put(
        `/user-management/users/${userId}/password`,
        passwordData,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to update user password:', error);
      throw new Error(error.response?.data?.message || 'Failed to update password');
    }
  }

  // Delete user
  async deleteUser(userId: string): Promise<any> {
    try {
      const response = await api.delete(
        `/user-management/users/${userId}`,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete user');
    }
  }

  // Update user details
  async updateUser(userId: string, data: Partial<UserDetails>): Promise<UserDetails> {
    try {
      const response = await api.put(
        `/user-management/users/${userId}`,
        data,
        { headers: this.getHeaders() }
      );
      return this.transformUserData(response.data);
    } catch (error: any) {
      console.error('Failed to update user:', error);
      throw new Error(error.response?.data?.message || 'Failed to update user');
    }
  }

  // Update user roles
  async updateUserRoles(userId: string, roles: string[]): Promise<UserDetails> {
    try {
      const response = await api.put(
        `/user-management/users/${userId}/roles`,
        { roles },
        { headers: this.getHeaders() }
      );
      return this.transformUserData(response.data);
    } catch (error: any) {
      console.error('Failed to update user roles:', error);
      throw new Error(error.response?.data?.message || 'Failed to update user roles');
    }
  }

  // Create new user (admin created)
  async createCustomUser(data: {
    name: string;
    email: string;
    password: string;
    role: string;
    userType: string;
    branchId?: string;
    customData?: any;
  }): Promise<User> {
    try {
      const response = await api.post(
        '/user-management/create-custom',
        data,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to create user:', error);
      throw new Error(error.response?.data?.message || 'Failed to create user');
    }
  }

  // Get user statistics
  async getUserStats(): Promise<{
    total: number;
    active: number;
    inactive: number;
    byType: Record<string, number>;
  }> {
    try {
      const response = await api.get('/user-management/stats', {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch user stats:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch user stats');
    }
  }

  // Search users
  async searchUsers(query: string): Promise<User[]> {
    try {
      const response = await api.get('/user-management/search', {
        headers: this.getHeaders(),
        params: { q: query },
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to search users:', error);
      throw new Error(error.response?.data?.message || 'Failed to search users');
    }
  }

  // Export users to CSV
  async exportUsers(filters?: {
    userType?: string;
    status?: string;
    branchId?: string;
  }): Promise<Blob> {
    try {
      const response = await api.get('/user-management/export', {
        headers: this.getHeaders(),
        params: filters,
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to export users:', error);
      throw new Error(error.response?.data?.message || 'Failed to export users');
    }
  }

  // Send password reset email
  async sendPasswordReset(userId: string): Promise<any> {
    try {
      const response = await api.post(
        `/user-management/users/${userId}/reset-password`,
        {},
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to send password reset:', error);
      throw new Error(error.response?.data?.message || 'Failed to send password reset');
    }
  }

  // Bulk send password reset
  async bulkSendPasswordReset(userIds: string[]): Promise<{
    success: number;
    failed: number;
    errors: Array<{ userId: string; error: string }>;
  }> {
    try {
      const response = await api.post(
        '/user-management/bulk-reset-password',
        { userIds },
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to bulk send password reset:', error);
      throw new Error(error.response?.data?.message || 'Failed to bulk send password reset');
    }
  }

  // Get user roles
  async getUserRoles(): Promise<Array<{ value: string; label: string }>> {
    try {
      const response = await api.get('/user-management/roles', {
        headers: this.getHeaders(),
      });
      return response.data.map((role: string) => ({
        value: role.toLowerCase().replace(/\s+/g, '_'),
        label: role,
      }));
    } catch (error: any) {
      console.error('Failed to fetch user roles:', error);
      // Return default roles
      return [
        'Academics User',
        'Accounts Manager',
        'Accounts User',
        'Agent',
        'Agriculture Manager',
        'Agriculture User',
        'Analytics',
        'Auditor',
        'Blogger',
        'Customer',
        'Dashboard Manager',
        'Employee',
        'Employee Self Service',
        'Expense Approver',
        'Fleet Manager',
        'Fulfillment User',
        'Helpdesk Contact',
        'HR Manager',
        'HR User',
        'Inbox User',
        'Interviewer',
        'Item Manager',
        'Knowledge Base Contributor',
        'Knowledge Base Editor',
        'Leave Approver',
        'Loan Manager',
        'Maintenance Manager',
        'Maintenance User',
        'Manufacturing Manager',
        'Manufacturing User',
        'Newsletter Manager',
        'Prepared Report User',
        'Projects Manager',
        'Projects User',
        'Purchase Manager',
        'Purchase Master Manager',
        'Purchase User',
        'Quality Manager',
        'Report Manager',
        'Sales Manager',
        'Sales Master Manager',
        'Sales User',
        'Script Manager',
        'Stock Manager',
        'Stock User',
        'Supplier',
        'Support Team',
        'System Manager',
        'Translator',
        'Website Manager',
        'Workspace Manager',
      ].map(role => ({
        value: role.toLowerCase().replace(/\s+/g, '_'),
        label: role,
      }));
    }
  }

  // Check if email exists
  async checkEmailExists(email: string): Promise<boolean> {
    try {
      const response = await api.get('/user-management/check-email', {
        headers: this.getHeaders(),
        params: { email },
      });
      return response.data.exists;
    } catch (error: any) {
      console.error('Failed to check email:', error);
      return false;
    }
  }

  // TIMELINE METHODS

  // Get user timeline
  async getUserTimeline(userId: string): Promise<TimelineItem[]> {
    try {
      const response = await api.get(`/user-management/users/${userId}/timeline`, {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch timeline:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch timeline');
    }
  }

  // Add timeline event
  async addUserTimelineEvent(userId: string, eventData: {
    actionType: string;
    actionDetails: any;
    metadata?: any;
  }): Promise<TimelineItem> {
    try {
      const response = await api.post(
        `/user-management/users/${userId}/timeline`,
        eventData,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to add timeline event:', error);
      throw new Error(error.response?.data?.message || 'Failed to add timeline event');
    }
  }

  // Add comment to user
  async addUserComment(userId: string, commentData: CommentData): Promise<any> {
    try {
      const response = await api.post(
        `/user-management/users/${userId}/comments`,
        commentData,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to add comment:', error);
      throw new Error(error.response?.data?.message || 'Failed to add comment');
    }
  }

  // Get user comments
  async getUserComments(userId: string): Promise<any[]> {
    try {
      const response = await api.get(`/user-management/users/${userId}/comments`, {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch comments:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch comments');
    }
  }

  // Get user attachments
  async getUserAttachments(userId: string): Promise<AttachmentData[]> {
    try {
      const response = await api.get(`/user-management/users/${userId}/attachments`, {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch attachments:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch attachments');
    }
  }

  // Upload user attachment
  async uploadUserAttachment(userId: string, file: File, description?: string): Promise<AttachmentData> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (description) {
        formData.append('description', description);
      }

      const response = await api.post(
        `/user-management/users/${userId}/attachments`,
        formData,
        {
          headers: this.getFormDataHeaders(),
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to upload attachment:', error);
      throw new Error(error.response?.data?.message || 'Failed to upload attachment');
    }
  }

  // Delete user attachment
  async deleteUserAttachment(userId: string, attachmentId: string): Promise<{ success: boolean }> {
    try {
      const response = await api.delete(
        `/user-management/users/${userId}/attachments/${attachmentId}`,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to delete attachment:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete attachment');
    }
  }

  // Helper methods
  private transformUserData(data: any): UserDetails {
    const nameParts = data.name?.split(' ') || ['', ''];
    const firstName = data.firstName || nameParts[0] || '';
    const lastName = data.lastName || nameParts.slice(-1)[0] || '';
    const middleName = data.middleName || nameParts.slice(1, -1).join(' ') || '';

    const rolesArray = Array.isArray(data.roles) 
      ? data.roles 
      : (data.allRoles || []).map((role: string) => ({
          value: role.toLowerCase().replace(/\s+/g, '_'),
          label: role,
          checked: true
        }));

    return {
      id: data.id,
      name: data.name,
      email: data.email,
      avatar: data.avatar || '/placeholder.svg?height=200&width=200',
      status: data.status || 'Active',
      userType: data.userType || 'User',
      isEnabled: data.isEnabled !== undefined ? data.isEnabled : true,
      role: data.role || 'user',
      tenantId: data.tenantId,
      branchId: data.branchId,
      branch: data.branch,
      tenant: data.tenant,
      metadata: data.metadata,
      firstName,
      middleName,
      lastName,
      username: data.username || data.email?.split('@')[0] || '',
      language: data.language || 'English',
      timeZone: data.timeZone || 'Africa/Lagos',
      enabled: data.enabled !== undefined ? data.enabled : true,
      roleProfile: data.roleProfile || '',
      roles: rolesArray,
      allRoles: data.allRoles || [],
      connections: data.connections || {
        profile: { contact: 1, blogger: 0 },
        logs: { accessLog: 15, activityLog: 11, energyPointLog: 0, routeHistory: 50 },
        settings: { userPermission: 2, documentFollow: 0 },
        activity: { communication: 0, toDo: 2 },
        integrations: { tokenCache: 0 },
      },
      assignedTo: data.assignedTo || 'PO',
      attachments: data.attachments || [],
      sharedWith: data.sharedWith || ['PO'],
      tags: data.tags || [],
      studentData: data.studentData,
      parentData: data.parentData,
      employeeData: data.employeeData,
      parentId: data.parentId,
      childrenCount: data.childrenCount,
      employeeId: data.employeeId,
    };
  }

  // Generate random password
  generateRandomPassword(length: number = 12): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  }

  // Validate password strength
  validatePassword(password: string): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (password.length < 6) {
      errors.push('Password must be at least 6 characters long');
    }

    if (!/[A-Z]/.test(password)) {
      errors.push('Password should contain at least one uppercase letter');
    }

    if (!/[a-z]/.test(password)) {
      errors.push('Password should contain at least one lowercase letter');
    }

    if (!/[0-9]/.test(password)) {
      errors.push('Password should contain at least one number');
    }

    return {
      valid: password.length >= 6 && errors.length === 0,
      errors,
    };
  }

  // Get user initials
  getUserInitials(name: string): string {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length === 0) return 'U';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  // Get user type color
  getUserTypeColor(userType: string): string {
    const type = userType.toLowerCase();
    switch (type) {
      case 'student':
        return 'text-primary border-primary/20';
      case 'staff':
      case 'teacher':
      case 'employee':
        return 'text-info border-info/20';
      case 'parent':
        return 'text-warning border-warning/20';
      case 'admin':
      case 'administrator':
      case 'branch-admin':
        return 'text-purple-500 border-purple-500/20';
      default:
        return 'text-muted-foreground border-muted-foreground/20';
    }
  }

   async getTenants(): Promise<SwitcherOption[]> {
    try {
      const response = await api.get('/user-management/tenants', {
        headers: this.getHeaders(),
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch tenants:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch tenants'
      );
    }
  }
 
  // Portal Switcher: users of a role within a target tenant (for impersonation)
  async getUsersByRole(
    tenantId: string,
    personRole: string
  ): Promise<SwitcherOption[]> {
    const role = personRole.toLowerCase();
    try {
      const response = await api.get('/user-management/portal-users', {
        headers: this.getHeaders(),
        params: { tenantId, role },
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch portal users:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch portal users'
      );
    }
  }
 

  // Get status color
  getStatusColor(status: string): string {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'active':
        return 'bg-success/10 text-success hover:bg-success/20';
      case 'inactive':
        return 'bg-secondary text-secondary-foreground hover:bg-secondary/80';
      case 'pending':
        return 'bg-warning/10 text-warning hover:bg-warning/20';
      case 'banned':
        return 'bg-destructive/10 text-destructive hover:bg-destructive/20';
      default:
        return 'bg-secondary text-secondary-foreground';
    }
  }
}

export const userManagementService = new UserManagementService();